import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Wordmark, Monogram } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/gift-edit", label: "The Gift Edit", testid: "nav-link-gift-edit" },
  { to: "/collection", label: "The Collection", testid: "nav-link-collection" },
  { to: "/concierge", label: "Private Concierge", testid: "nav-link-concierge" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/faqs", label: "FAQs", testid: "nav-link-faqs" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const dark = location.pathname === "/concierge" && !scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-cream/90 backdrop-blur-md border-b border-chocolate/10 py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Salem & Sabrina — home">
            <Wordmark compact={scrolled} dark={dark} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:text-golddark ${
                    isActive
                      ? dark ? "text-champagne" : "text-golddark"
                      : dark ? "text-cream/70 hover:text-champagne" : "text-chocolate/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/gift-edit#brief"
              data-testid="nav-cta-start-gift-edit"
              className={`group inline-flex items-center gap-2 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                dark ? "bg-gold text-espresso hover:bg-champagne" : "bg-chocolate text-cream hover:bg-espresso"
              }`}
            >
              Start a Gift Edit
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-menu-button"
            className={`lg:hidden p-2 ${dark ? "text-cream" : "text-chocolate"}`}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-drawer"
            className="fixed inset-0 z-[70] bg-cream flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-4 sm:px-8 py-5 border-b border-chocolate/10">
              <Monogram size={38} />
              <button data-testid="nav-mobile-close-button" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 text-chocolate">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.testid}-mobile`}
                    className={({ isActive }) =>
                      `font-serif text-4xl py-2 block transition-colors ${isActive ? "text-golddark italic" : "text-chocolate"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="pt-8"
              >
                <Link
                  to="/gift-edit#brief"
                  data-testid="nav-mobile-cta"
                  className="inline-flex items-center gap-2 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em]"
                >
                  Start a Gift Edit <ArrowUpRight size={14} />
                </Link>
              </motion.div>
            </nav>
            <div className="px-8 py-6 border-t border-chocolate/10 font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/50">
              Personalised Luxury Gifting
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
